<script lang="ts">
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import { removeAllErrors } from "@kontextwerk/web-sdk/core/forms/formValidation"
  import { untrack, type Snippet } from "svelte"
  import { onMount } from "svelte"
  import PropertyUnitSelect from "./_modules/property-unit-select"
  import WorkOrderTypeFields from "./_modules/work-order-type-fields"
  import {
    findPropertyById,
    normalizeId,
    sanitizePropertyUnitSelection,
    type PropertyUnitSelection,
  } from "../../_logic/property-unit-selection"
  import type { WorkOrderFormValue } from "../../_logic/work-order-form-types"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiContentSave, mdiPlus } from "@mdi/js"
  import { formatDateOnly } from "../../../../_shared/_logic/timezones"

  type EmployeeOption = { value: string; label: string }

  export type { WorkOrderFormValue } from "../../_logic/work-order-form-types"

  const resetForm = (orderNumber = ""): WorkOrderFormValue => ({
    property_id: "",
    property_unit_id: "",
    order_number: orderNumber,
    order_type_ids: [],
    custom_order_type: null,
    due_date: "",
    priority: "",
    estimated_duration_min: "",
    allowed_employee_ids: [],
  })

  type Mode = "create" | "edit" | "view"

  type FormContext<T> = {
    data: T
    setData: (next: T) => void
    updateData: (next: Partial<T>) => void
    submitting: boolean
    loading: boolean
    mode: Mode
    submit: () => Promise<void>
    reset: () => Promise<void>
  }

  type Props = {
    organizationId?: string | null
    properties?: Property[]
    customers?: Customer[]
    orderTypes?: OrderType[]
    employees?: EmployeeOption[]
    defaultOrderNumber?: string | null
    disabled?: boolean
    brightBackground?: boolean
    showPropertySelect?: boolean
    requireProperty?: boolean
    showOrderNumber?: boolean
    showOrderTypeFields?: boolean
    showAllowedEmployees?: boolean
    fieldIds?: Partial<FieldIds>
    theme?: "dark" | "light"
    submitLabel?: string
    showReset?: boolean
    showSubmit?: boolean
    mode?: Mode
    propertySelectionDisabled?: boolean
    orderNumberDisabled?: boolean
    orderTypeFieldsDisabled?: boolean
    dueDateDisabled?: boolean
    allowedEmployeesDisabled?: boolean
    actionButtonSnippet?: Snippet<[FormContext<WorkOrderFormValue>]>
    setSubmitFn?: (submit: () => Promise<void>) => void
    submitting?: boolean
    onSubmit?: (data: WorkOrderFormValue) => void | Promise<void>
    onReset?: (data: WorkOrderFormValue) => void | Promise<void>
    onChange?: (payload: { data: WorkOrderFormValue; valid: boolean }) => void
    validateData?: (args: {
      data: WorkOrderFormValue
      form: HTMLFormElement | null
    }) => boolean | Promise<boolean>
  }

  type FieldIds = {
    property: string
    propertyUnit: string
    orderNumber: string
    orderType: string
    dueDate: string
    priority: string
    estimatedDuration: string
    allowedEmployees: string
  }

  const defaultFieldIds: FieldIds = {
    property: "work-order-property",
    propertyUnit: "work-order-property-unit",
    orderNumber: "work-order-number",
    orderType: "work-order-type",
    dueDate: "work-order-due-date",
    priority: "work-order-priority",
    estimatedDuration: "work-order-estimated-duration",
    allowedEmployees: "work-order-allowed-employees",
  }

  type PublicProps = Props & { value?: WorkOrderFormValue; valid?: boolean }

  let {
    organizationId = null,
    properties = [],
    customers = [],
    orderTypes = [],
    employees = [],
    defaultOrderNumber = null,
    disabled = false,
    brightBackground = true,
    showPropertySelect = true,
    requireProperty = true,
    showOrderNumber = true,
    showOrderTypeFields = true,
    showAllowedEmployees = true,
    fieldIds = {},
    theme = "dark",
    submitLabel = undefined,
    showReset = true,
    showSubmit = true,
    mode = "create",
    propertySelectionDisabled = false,
    orderNumberDisabled = false,
    orderTypeFieldsDisabled = false,
    dueDateDisabled = false,
    allowedEmployeesDisabled = false,
    actionButtonSnippet = undefined,
    setSubmitFn = undefined,
    onSubmit = async () => {},
    onReset = async () => {},
    onChange = () => {},
    validateData = undefined,
    value: form = $bindable(resetForm(defaultOrderNumber ?? "")),
    valid: formValid = $bindable(false),
    submitting = $bindable(false),
  }: PublicProps = $props()

  const toTrimmedString = (value: unknown) => {
    if (typeof value === "string") return value.trim()

    if (value === null || value === undefined) return ""

    return String(value).trim()
  }

  const normalizeDateInputValue = (raw: unknown): string =>
    formatDateOnly(raw as string | Date | number | null | undefined)

  const normalizeAllowedEmployeeSelection = (raw: unknown): string[] => {
    if (!Array.isArray(raw)) return []

    return raw.map((entry) => toTrimmedString(entry)).filter(Boolean)
  }

  const stringArraysEqual = (left: string[], right: string[]) =>
    left.length === right.length && left.every((entry, index) => entry === right[index])

  const resolvedFieldIds = $derived<FieldIds>({ ...defaultFieldIds, ...fieldIds })

  const normalizeAllowedEmployees = (
    values: unknown,
    { fallbackToAll }: { fallbackToAll?: boolean } = {},
  ): string[] => {
    const selected = Array.isArray(values) ? values : []
    const normalizedSelection = selected
      .map((value) => toTrimmedString(value))
      .filter((value): value is string => Boolean(value))

    const preferred =
      normalizedSelection.length || !fallbackToAll
        ? normalizedSelection
        : employees.map((opt) => toTrimmedString(opt.value))

    const seen = new Set<string>()
    const normalized: string[] = []
    for (const value of preferred) {
      const trimmed = toTrimmedString(value)

      if (!trimmed || seen.has(trimmed)) continue

      seen.add(trimmed)
      normalized.push(trimmed)
    }

    return normalized
  }

  const initialDefaultOrderNumber = untrack(() => defaultOrderNumber ?? "")
  const initialProperties = untrack(() => properties)
  const initialShowPropertySelect = untrack(() => showPropertySelect)

  let formElement = $state<HTMLFormElement | null>(null)
  let loading = $state(false)

  const cloneForm = (value: WorkOrderFormValue): WorkOrderFormValue => ({
    ...value,
    order_type_ids: [...(value.order_type_ids ?? [])],
    custom_order_type: value.custom_order_type ? { ...value.custom_order_type } : null,
    allowed_employee_ids: [...(value.allowed_employee_ids ?? [])],
  })

  const initialFormSeed = sanitizePropertyUnitSelection(
    {
      ...resetForm(initialDefaultOrderNumber),
      ...form,
      order_type_ids: [...((form as any)?.order_type_ids ?? [])],
      custom_order_type: (form as any)?.custom_order_type
        ? { ...(form as any).custom_order_type }
        : null,
      estimated_duration_min: toTrimmedString((form as any)?.estimated_duration_min),
      allowed_employee_ids: normalizeAllowedEmployees((form as any)?.allowed_employee_ids),
    },
    initialProperties,
    initialShowPropertySelect,
  )

  const buildInitForm = (seed: WorkOrderFormValue): WorkOrderFormValue => {
    const orderNumber =
      seed.order_number?.trim() || initialDefaultOrderNumber || seed.order_number || ""

    return sanitizePropertyUnitSelection(
      {
        ...seed,
        order_type_ids: [...(seed.order_type_ids ?? [])],
        custom_order_type: seed.custom_order_type ? { ...seed.custom_order_type } : null,
        order_number: orderNumber ?? "",
        estimated_duration_min: toTrimmedString(seed.estimated_duration_min),
        allowed_employee_ids: normalizeAllowedEmployees(seed.allowed_employee_ids),
      },
      initialProperties,
      initialShowPropertySelect,
    )
  }

  const initialHydratedForm = buildInitForm(initialFormSeed)
  let selectedProperty = $state<Property | null>(
    findPropertyById(initialProperties, initialHydratedForm.property_id) ?? null,
  )
  let propertySelection = $state<PropertyUnitSelection>({
    property_id: normalizeId(initialHydratedForm.property_id),
    property_unit_id: normalizeId(initialHydratedForm.property_unit_id),
  })
  let dueDateValue = $state<string>(normalizeDateInputValue(initialHydratedForm.due_date))
  let allowedEmployeesValue = $state<string[]>(
    normalizeAllowedEmployeeSelection(initialHydratedForm.allowed_employee_ids),
  )

  const resolveSelectedProperty = (propertyId: string) => {
    const normalizedPropertyId = normalizeId(propertyId)

    if (!normalizedPropertyId) return null

    if (selectedProperty && normalizeId(selectedProperty.id) === normalizedPropertyId)
      return selectedProperty

    return findPropertyById(properties, normalizedPropertyId)
  }

  const computeValidity = (value: WorkOrderFormValue): boolean => {
    // Property selection is optional for batch uploads.
    if (showPropertySelect && requireProperty) {
      const propertyId = toTrimmedString(value.property_id)

      if (!propertyId) return false

      const resolvedProperty = resolveSelectedProperty(propertyId)
      const requiresUnit = Boolean(resolvedProperty && (resolvedProperty.units?.length ?? 0) > 0)

      if (requiresUnit && !toTrimmedString(value.property_unit_id)) return false
    }

    return Boolean(
      value.order_type_ids.length > 0 || value.custom_order_type?.internal_label?.trim(),
    )
  }

  const buildSnapshot = (): WorkOrderFormValue => ({
    ...cloneForm(form),
    due_date: normalizeDateInputValue(dueDateValue),
    allowed_employee_ids: normalizeAllowedEmployeeSelection(allowedEmployeesValue),
  })

  const emitChange = () => {
    const snapshot = buildSnapshot()
    const nextValid = computeValidity(snapshot)
    formValid = nextValid
    onChange?.({ data: snapshot, valid: nextValid })

    return { snapshot, valid: nextValid }
  }

  const handleDueDateChange = () => {
    const nextValue = normalizeDateInputValue(dueDateValue)
    dueDateValue = nextValue
    form = { ...form, due_date: nextValue }
    emitChange()
  }

  const handleAllowedEmployeesChange = () => {
    const nextValue = normalizeAllowedEmployeeSelection(allowedEmployeesValue)
    allowedEmployeesValue = nextValue
    form = { ...form, allowed_employee_ids: nextValue }
    emitChange()
  }

  const runValidation = async (data: WorkOrderFormValue) => {
    if (!validateData) return true

    const result = validateData({ data, form: formElement })

    return result instanceof Promise ? await result : result
  }

  const handleSubmit = async (event?: Event) => {
    event?.preventDefault()

    if (disabled || submitting || loading) return

    removeAllErrors(formElement ?? undefined)
    const snapshot = buildSnapshot()
    const nextValid = computeValidity(snapshot)
    formValid = nextValid
    const valid = await runValidation(snapshot)

    if (!valid) return

    submitting = true
    try {
      onChange?.({ data: snapshot, valid: nextValid })
      await onSubmit(snapshot)
    } catch (err) {
      console.error("work_order_form_submit_failed", err)
    } finally {
      submitting = false
    }
  }

  const handleReset = async () => {
    if (disabled || submitting || loading) return

    removeAllErrors(formElement ?? undefined)
    const next = cloneForm(initialHydratedForm)
    form = next
    propertySelection = {
      property_id: normalizeId(next.property_id),
      property_unit_id: normalizeId(next.property_unit_id),
    }
    dueDateValue = normalizeDateInputValue(next.due_date)
    allowedEmployeesValue = normalizeAllowedEmployeeSelection(next.allowed_employee_ids)
    emitChange()

    try {
      await onReset(next)
    } catch (err) {
      console.error("work_order_form_reset_failed", err)
    }
  }

  const context = $derived<FormContext<WorkOrderFormValue>>({
    data: form,
    setData: (next) => {
      form = next
      propertySelection = {
        property_id: normalizeId(next.property_id),
        property_unit_id: normalizeId(next.property_unit_id),
      }
      dueDateValue = normalizeDateInputValue(next.due_date)
      allowedEmployeesValue = normalizeAllowedEmployeeSelection(next.allowed_employee_ids)
      emitChange()
    },
    updateData: (next) => {
      form = { ...form, ...next }

      if ("property_id" in next || "property_unit_id" in next) {
        propertySelection = {
          property_id: normalizeId((next.property_id ?? form.property_id) as string),
          property_unit_id: normalizeId((next.property_unit_id ?? form.property_unit_id) as string),
        }
      }

      if ("due_date" in next) {
        dueDateValue = normalizeDateInputValue(next.due_date)
      }

      if ("allowed_employee_ids" in next) {
        allowedEmployeesValue = normalizeAllowedEmployeeSelection(next.allowed_employee_ids)
      }

      emitChange()
    },
    submitting,
    loading,
    mode,
    submit: () => handleSubmit(),
    reset: handleReset,
  })

  const effectiveSubmitLabel = $derived(submitLabel ?? (mode === "edit" ? "Speichern" : "Anlegen"))
  const formDisabled = $derived(Boolean(disabled || submitting || loading))
  const propertyFieldsDisabled = $derived(Boolean(formDisabled || propertySelectionDisabled))
  const orderNumberFieldDisabled = $derived(Boolean(formDisabled || orderNumberDisabled))
  const orderTypeFieldsetDisabled = $derived(Boolean(formDisabled || orderTypeFieldsDisabled))
  const dueDateFieldDisabled = $derived(Boolean(formDisabled || dueDateDisabled))
  const allowedEmployeesFieldDisabled = $derived(Boolean(formDisabled || allowedEmployeesDisabled))

  onMount(() => {
    setSubmitFn?.(() => handleSubmit())
  })
  // Align external bindings with our sanitized initial state.
  form = cloneForm(initialHydratedForm)
  propertySelection = {
    property_id: normalizeId(form.property_id),
    property_unit_id: normalizeId(form.property_unit_id),
  }
  dueDateValue = normalizeDateInputValue(form.due_date)
  allowedEmployeesValue = normalizeAllowedEmployeeSelection(form.allowed_employee_ids)
  formValid = computeValidity(form)

  $effect(() => {
    const normalizedDueDate = normalizeDateInputValue(form.due_date)

    if (dueDateValue !== normalizedDueDate) {
      dueDateValue = normalizedDueDate
    }

    const normalizedAllowedEmployees = normalizeAllowedEmployeeSelection(form.allowed_employee_ids)

    if (!stringArraysEqual(allowedEmployeesValue, normalizedAllowedEmployees)) {
      allowedEmployeesValue = normalizedAllowedEmployees
    }
  })
</script>

<form
  class={`form ${theme}`}
  bind:this={formElement}
  onsubmit={(event) => {
    event.preventDefault()
  }}
>
  <div class="formContent">
    {#if showPropertySelect}
      <PropertyUnitSelect
        {organizationId}
        {properties}
        bind:selectedProperty
        value={propertySelection}
        {showPropertySelect}
        {brightBackground}
        disabled={propertyFieldsDisabled}
        fieldIds={{
          property: resolvedFieldIds.property,
          propertyUnit: resolvedFieldIds.propertyUnit,
        }}
        onChange={(value: PropertyUnitSelection) => {
          propertySelection = value
          form = {
            ...form,
            property_id: value.property_id,
            property_unit_id: value.property_unit_id,
          }
          emitChange()
        }}
      />
    {/if}
    {#if showOrderNumber}
      <section class="row">
        {#if showOrderNumber}
          <Input
            id={resolvedFieldIds.orderNumber}
            placeholder="Auftragsnummer"
            bind:value={form.order_number}
            disabled={orderNumberFieldDisabled}
            tooltip="Interne oder externe Auftragsnummer zur Identifikation des Auftrags."
            onChange={emitChange}
          />
        {/if}
      </section>
    {/if}
    {#if showOrderTypeFields}
      <WorkOrderTypeFields
        {orderTypes}
        {brightBackground}
        disabled={orderTypeFieldsetDisabled}
        fieldIds={{
          orderType: resolvedFieldIds.orderType,
          estimatedDuration: resolvedFieldIds.estimatedDuration,
        }}
        value={{
          order_type_ids: form.order_type_ids,
          custom_order_type: form.custom_order_type,
          estimated_duration_min: form.estimated_duration_min,
        }}
        onChange={({
          value,
        }: {
          value: {
            order_type_ids: string[]
            custom_order_type: WorkOrderCustomOrderType | null
            estimated_duration_min: string
          }
        }) => {
          form = {
            ...form,
            order_type_ids: value.order_type_ids,
            custom_order_type: value.custom_order_type,
            estimated_duration_min: value.estimated_duration_min,
          }
          emitChange()
        }}
      />
    {/if}
    <section class="row" style="margin-top: 0.6rem;">
      <Input
        id={resolvedFieldIds.dueDate}
        type="date"
        placeholder="Gesetzliche Frist"
        bind:value={dueDateValue}
        {brightBackground}
        disabled={dueDateFieldDisabled}
        tooltip="Frist für den Abschluss des Auftrags. Wird für Fristüberwachung und Aufgaben verwendet."
        onChange={() => handleDueDateChange()}
      />
      {#if showAllowedEmployees && employees.length}
        <Input
          id={resolvedFieldIds.allowedEmployees}
          type="select"
          placeholder="Zugelassene Mitarbeiter"
          options={employees}
          multiple={true}
          bind:value={allowedEmployeesValue}
          helperText="Keine Auswahl = alle Mitarbeiter. Nur diese Auswahl wird für Vorschläge, Bots & Planung berücksichtigt."
          {brightBackground}
          tooltip="Nur diese Mitarbeiter werden für die Terminplanung berücksichtigt."
          disabled={allowedEmployeesFieldDisabled}
          onChange={() => handleAllowedEmployeesChange()}
        />
      {/if}
    </section>
  </div>

  {#if actionButtonSnippet || showReset || showSubmit}
    <section class="formActions">
      {#if actionButtonSnippet}
        <div class="formActions__secondary">
          {@render actionButtonSnippet(context)}
        </div>
      {/if}
      <div class="formActions__primary" class:showReset>
        {#if showReset}
          <CTA
            type="secondaryButton"
            content="Zurücksetzen"
            disabled={formDisabled}
            onClick={handleReset}
          />
        {/if}
        {#if showSubmit}
          <CTA
            type="primaryButton"
            content={effectiveSubmitLabel}
            disabled={formDisabled}
            onClick={handleSubmit}
          >
            {#snippet iconSnippet()}
              <Icon path={mode == "edit" ? mdiContentSave : mdiPlus} />
            {/snippet}
          </CTA>
        {/if}
      </div>
    </section>
  {/if}
</form>

<style lang="less">
  .row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 0.75rem;
  }

  .formContent {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
  }

  .formActions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;

    .formActions__primary {
      display: flex;
      gap: 0.6rem;
      align-items: center;
      justify-content: flex-end;
      width: 100%;
      &.showReset {
        justify-content: space-between;
        width: 100%;
      }
    }
  }
</style>
